import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { loadProspect } from '@/lib/prospect-loader'
import SupportPanel from '@/components/support/SupportPanel'
import { useAuth } from '@/lib/auth-context'

type ProspectProfile = {
  id: number
  handle: string
  display_name?: string | null
  email?: string | null
  status?: string | null
  notes?: {
    annual_budget_cents?: number | null
    spent_cents?: number | null
    wallet_credits?: number | null
    monthly_remaining_cents?: number | null
    shopping_sessions?: ShoppingSessionRecord[]
  } | null
}

type ShoppingSessionRecord = {
  id: string | number
  partner?: string | null
  status?: string | null
  budget_cents?: number | null
}

type SessionStateType = {
  loading: boolean
  error: string
  message: string
  session: {
    email_alias?: string | null
    card_last4?: string | null
    card_exp_month?: string | null
    card_exp_year?: string | null
  } | null
}

type PortalState = {
  status: 'loading' | 'ready' | 'error'
  profile: ProspectProfile | null
  error: { code?: string; message?: string } | null
  readOnly: boolean
}

type ProspectError = {
  code?: string
  message?: string
}

const TABS = [
  { id: 'home', label: 'Home' },
  { id: 'budget', label: 'Budget & Wallet' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'ai', label: 'AI Tools' },
  { id: 'support', label: 'Support' },
]

function deriveErrorState(error: ProspectError | null) {
  if (!error) return { heading: 'Unable to load prospect', message: 'Something went wrong.' }

  const code = error.code || error.message
  switch (code) {
    case 'not_authenticated':
      return {
        heading: 'Sign-in required',
        message:
          'This invite link requires an authenticated session. Please open the magic link sent to your email to continue.',
      }
    case 'forbidden':
      return {
        heading: 'No access',
        message: 'This prospect profile is linked to a different account.',
      }
    case 'not_found':
      return {
        heading: 'Not found',
        message: 'We could not find a prospect with that handle.',
      }
    default:
      return {
        heading: 'Something went wrong',
        message: error.message || 'An unexpected error occurred.',
      }
  }
}

function formatCurrency(cents: number | null | undefined) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((cents as number) / 100)
}

function ProgressBar({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total && value ? Math.min(Math.round((value / total) * 100), 100) : 0
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-cocoa/60">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-shell">
        <div className="h-full rounded-full bg-cocoa" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function BudgetCard({ annualBudget, spent, walletCredits }: { annualBudget: number | null; spent: number | null; walletCredits: number | null }) {
  const remaining = Number.isFinite(annualBudget) && Number.isFinite(spent) ? Math.max((annualBudget as number) - (spent as number), 0) : null
  const monthly = remaining !== null && Number.isFinite(remaining) ? Math.round(remaining / 12) : null
  return (
    <div className="space-y-4 rounded-2xl bg-white/80 p-6 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-cocoa">Annual budget</h3>
          <p className="text-sm text-cocoa/70">Keep an eye on what’s left for the year.</p>
        </div>
        <span className="rounded-full bg-shell px-3 py-1 text-xs font-semibold uppercase text-cocoa/70">
          {formatCurrency(annualBudget)} / year
        </span>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl bg-shell/70 p-4">
          <p className="text-xs uppercase tracking-wide text-cocoa/60">Spent</p>
          <p className="text-lg font-semibold text-cocoa">{formatCurrency(spent)}</p>
        </div>
        <div className="rounded-xl bg-shell/70 p-4">
          <p className="text-xs uppercase tracking-wide text-cocoa/60">Remaining</p>
          <p className="text-lg font-semibold text-cocoa">{formatCurrency(remaining)}</p>
        </div>
        <div className="rounded-xl bg-shell/70 p-4">
          <p className="text-xs uppercase tracking-wide text-cocoa/60">Monthly pace</p>
          <p className="text-lg font-semibold text-cocoa">{formatCurrency(monthly)}</p>
        </div>
      </div>
      <ProgressBar label="Year-to-date" value={spent || 0} total={annualBudget || 0} />
      <div className="rounded-xl border border-sand bg-white p-4">
        <p className="text-xs uppercase tracking-wide text-cocoa/60">Wallet credits</p>
        <p className="text-lg font-semibold text-cocoa">{Number.isFinite(walletCredits) ? walletCredits : '—'} credits</p>
        <p className="mt-2 text-xs text-cocoa/70">Recent ledger activity will appear here.</p>
      </div>
    </div>
  )
}

function ShoppingSection({
  prospect,
  token,
  sessionState,
  budgetInput,
  onBudgetChange,
  onStart,
  sessions,
}: {
  prospect: ProspectProfile
  token: string
  sessionState: SessionStateType
  budgetInput: string
  onBudgetChange: (value: string) => void
  onStart: () => void
  sessions: ShoppingSessionRecord[]
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-cocoa">Start a shopping session</h3>
            <p className="text-sm text-cocoa/70">
              We’ll create a one-time shopping alias tied to {prospect.display_name || prospect.handle}’s budget.
            </p>
          </div>
          <span className="rounded-full bg-shell px-3 py-1 text-xs font-semibold uppercase text-cocoa/70">
            Privacy card ready
          </span>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[2fr,1fr,auto] md:items-end">
          <label className="flex flex-col gap-2 text-sm font-medium text-cocoa">
            Estimated spend (USD)
            <input
              value={budgetInput}
              onChange={(event) => onBudgetChange(event.target.value)}
              placeholder="Optional — e.g. 250"
              inputMode="decimal"
              className="rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
            />
          </label>
          <div className="rounded-2xl bg-shell/70 p-4 text-sm text-cocoa/80">
            <p className="font-semibold text-cocoa">How it works</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>We confirm budget availability.</li>
              <li>We reuse your active privacy card or create one.</li>
              <li>Confirmation is emailed to your purchase alias.</li>
            </ul>
          </div>
          <button
            type="button"
            onClick={onStart}
            disabled={sessionState.loading || !token}
            className="Button primary whitespace-nowrap"
          >
            {sessionState.loading ? 'Starting…' : 'Start session'}
          </button>
        </div>
        {sessionState.error ? <p className="mt-3 text-sm text-brick">{sessionState.error}</p> : null}
        {sessionState.message ? <p className="mt-3 text-sm text-emerald-700">{sessionState.message}</p> : null}
        {sessionState.session ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">Session ready</p>
            <p className="mt-1">Alias: {sessionState.session.email_alias || 'sent via email'}</p>
            {sessionState.session.card_last4 ? (
              <p className="text-xs text-emerald-800">
                Card ending in {sessionState.session.card_last4} ({sessionState.session.card_exp_month}/{sessionState.session.card_exp_year})
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-cocoa">Past sessions</h4>
            <p className="text-sm text-cocoa/70">Recent shopping activity for this prospect.</p>
          </div>
        </div>
        {sessions && sessions.length ? (
          <ul className="mt-4 space-y-3 text-sm text-cocoa/80">
            {sessions.map((session) => (
              <li key={session.id} className="rounded-xl border border-sand bg-shell/60 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-cocoa">{session.partner || 'Vendor'}</span>
                  <span className="rounded-full bg-cocoa/10 px-2 py-1 text-xs font-semibold uppercase text-cocoa/80">
                    {session.status || 'Scheduled'}
                  </span>
                </div>
                <p className="text-xs text-cocoa/60">Budget {formatCurrency(session.budget_cents)}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-cocoa/70">No sessions yet. Start one to receive a purchase alias.</p>
        )}
      </div>
    </div>
  )
}

function ProspectSummary({ profile, readOnly }: { profile: ProspectProfile; readOnly: boolean }) {
  return (
    <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-cocoa">Welcome, {profile.display_name || profile.handle}</h1>
          <p className="text-sm text-cocoa/70">Handle: {profile.handle}</p>
          <p className="text-sm text-cocoa/70">Status: {profile.status}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide text-cocoa/60">
          <span className="rounded-full bg-shell px-3 py-1">Credits balance</span>
          <span className="rounded-full bg-shell px-3 py-1">Remaining this month</span>
        </div>
      </div>
      {profile.email ? <p className="mt-2 text-sm text-cocoa/70">Email on file: {profile.email}</p> : null}
      {readOnly ? (
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Developer bypass active — showing read-only data.
        </div>
      ) : null}
    </div>
  )
}

export default function ProspectPortal() {
  const { handle } = useParams()
  const [searchParams] = useSearchParams()
  const { session, profile: authProfile } = useAuth()
  const [state, setState] = useState<PortalState>({ status: 'loading', profile: null, error: null, readOnly: false })
  const [activeTab, setActiveTab] = useState('home')
  const [budgetInput, setBudgetInput] = useState('')
  const [sessionState, setSessionState] = useState<SessionStateType>({ loading: false, error: '', message: '', session: null })

  const devKey = searchParams.get('dev_key')
  const allowDevDoor = useMemo(() => {
    if (!devKey) return false
    const expected = import.meta.env.VITE_DEV_PROSPECT_KEY
    const nonProd = import.meta.env.DEV || import.meta.env.MODE !== 'production'
    return Boolean(nonProd && expected && devKey === expected)
  }, [devKey])

  const supportContextMeta = useMemo(
    () => ({ surface: 'prospect_portal' as const, prospect_id: state.profile?.id || undefined }),
    [state.profile?.id],
  )

  const supportMetadata = useMemo(() => {
    const role = authProfile?.role || 'prospect'
    return { ...supportContextMeta, context: 'locker_room', role }
  }, [authProfile?.role, supportContextMeta])

  useEffect(() => {
    let cancelled = false

    async function fetchProfile() {
      setState({ status: 'loading', profile: null, error: null, readOnly: false })

      try {
        if (!handle) {
          throw Object.assign(new Error('Prospect handle missing'), { code: 'missing_handle' })
        }

        if (allowDevDoor) {
          const params = new URLSearchParams({ handle, dev_key: devKey || '' })
          const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
          const res = await fetch(`${CORE_API}/api/v1/admin/prospect-profile?${params.toString()}`)
          if (!res.ok) {
            const text = await res.text()
            throw Object.assign(new Error(text || 'Unable to load prospect'), { code: 'dev_fetch_failed' })
          }
          const data = await res.json()
          if (cancelled) return
          setState({ status: 'ready', profile: data, error: null, readOnly: true })
          return
        }

        const data = await loadProspect(handle) as unknown as ProspectProfile
        if (cancelled) return
        setState({ status: 'ready', profile: data, error: null, readOnly: false })
      } catch (error: any) {
        if (cancelled) return
        setState({ status: 'error', profile: null, error, readOnly: allowDevDoor })
      }
    }

    fetchProfile()

    return () => {
      cancelled = true
    }
  }, [handle, allowDevDoor, devKey])

  const startShopping = async () => {
    if (!session?.access_token || !state.profile?.id) return
    setSessionState({ loading: true, error: '', message: '', session: null })
    const numeric = budgetInput.replace(/[^\d.]/g, '')
    const parsed = numeric ? Number.parseFloat(numeric) : null
    const cents = parsed !== null && Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null

    try {
      const CORE_API_SHOP = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API_SHOP}/api/v1/shopping/prospect-start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ prospect_id: state.profile.id, estimated_budget_cents: cents || undefined }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to start session')
      }
      setSessionState({
        loading: false,
        error: '',
        message: 'Your shopping link has been emailed.',
        session: payload.session || null,
      })
      setBudgetInput('')
      setActiveTab('shopping')
    } catch (error) {
      setSessionState({ loading: false, error: error instanceof Error ? error.message : 'Unable to start session', message: '', session: null })
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="rounded-2xl border border-sand/60 bg-white/70 px-6 py-4 text-cocoa/70">Loading your prospect portal…</div>
      </div>
    )
  }

  if (state.status === 'error') {
    const info = deriveErrorState(state.error)
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="max-w-lg rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700">
          <h2 className="text-lg font-semibold">{info.heading}</h2>
          <p className="mt-2 text-sm">{info.message}</p>
          {state.readOnly ? (
            <p className="mt-3 text-xs text-rose-500">Developer bypass was attempted but failed.</p>
          ) : null}
        </div>
      </div>
    )
  }

  if (!state.profile) {
    return null
  }

  const notes = state.profile.notes || {}
  const annualBudget: number | null = Number.isFinite(notes.annual_budget_cents) ? notes.annual_budget_cents! : null
  const spent: number | null = Number.isFinite(notes.spent_cents) ? notes.spent_cents! : null
  const walletCredits: number | null = Number.isFinite(notes.wallet_credits) ? notes.wallet_credits! : null
  const sessions = Array.isArray(notes.shopping_sessions) ? notes.shopping_sessions : []

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <ProspectSummary profile={state.profile} readOnly={state.readOnly} />

      <nav className="flex flex-wrap gap-2 text-sm">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-full px-3 py-1 font-semibold transition ${
              activeTab === tab.id ? 'bg-cocoa text-white shadow-soft' : 'bg-shell text-cocoa/80 hover:bg-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'home' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
            <h3 className="text-lg font-semibold text-cocoa">Today’s focus</h3>
            <p className="mt-2 text-sm text-cocoa/70">
              Set your weekly goal, check your budget, and plan any rewards you want to unlock.
            </p>
            <div className="mt-4 space-y-2 text-sm text-cocoa/80">
              <p>• Keep families updated with a new highlight.</p>
              <p>• Confirm your next shopping session window.</p>
              <p>• Try an AI tool to draft your next recap.</p>
            </div>
          </div>
          <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
            <h3 className="text-lg font-semibold text-cocoa">Quick balances</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl bg-shell/70 p-4">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Credits balance</p>
                <p className="text-lg font-semibold text-cocoa">{Number.isFinite(walletCredits) ? walletCredits : '—'} credits</p>
              </div>
              <div className="rounded-xl bg-shell/70 p-4">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Remaining this month</p>
                <p className="text-lg font-semibold text-cocoa">{formatCurrency(notes.monthly_remaining_cents)}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-cocoa/70">Budget updates reflect current-year spend and privacy card usage.</p>
          </div>
        </div>
      ) : null}

      {activeTab === 'budget' ? (
        <BudgetCard annualBudget={annualBudget} spent={spent} walletCredits={walletCredits} />
      ) : null}

      {activeTab === 'shopping' ? (
        <ShoppingSection
          prospect={state.profile}
          token={session?.access_token || ''}
          sessionState={sessionState}
          budgetInput={budgetInput}
          onBudgetChange={setBudgetInput}
          onStart={startShopping}
          sessions={sessions}
        />
      ) : null}

      {activeTab === 'ai' ? (
        <div className="rounded-2xl bg-white/80 p-6 shadow-soft">
          <h3 className="text-lg font-semibold text-cocoa">AI tools</h3>
          <p className="mt-2 text-sm text-cocoa/70">
            Coming soon – goal planner, progress recap, and highlight generator tailored for your Locker Room profile.
          </p>
        </div>
      ) : null}

      {activeTab === 'support' ? (
        <SupportPanel
          roleLabel="Prospect"
          intro="Questions about onboarding or sponsorship? The Tower team can help here."
          defaultCategory="prospect_support"
          contextMeta={supportContextMeta}
          defaultMetadata={supportMetadata || undefined}
          prospectId={state.profile.id}
        />
      ) : null}
    </div>
  )
}
